import { ROUTES } from './constants';
import {
  ACCRUAL_BOOKS_BUNDLE_APP_IDS,
  ACTIVITY_LOGS_APP_ID,
  BILLS_APP_ID,
  CAPITALS_APP_ID,
  CASH_DISBURSEMENTS_JOURNAL_APP_ID,
  CASH_RECEIPTS_JOURNAL_APP_ID,
  CHART_OF_ACCOUNTS_APP_ID,
  CUSTOM_ENTRIES_APP_ID,
  CUSTOMERS_APP_ID,
  FINANCIAL_STATEMENTS_APP_ID,
  GENERAL_LEDGER_APP_ID,
  INVOICES_APP_ID,
  LEDGER_REPORTS_APP_ID,
  NON_VAT_ACCRUAL_BOOKS_APP_ID,
  NON_VAT_BOOKS_APP_ID,
  PPE_APP_ID,
  SUPPLIERS_APP_ID,
  UPLOAD_ENTRY_APP_ID,
} from './companyAppIds';

export * from './companyAppIds';

export const ACCOUNTING_APP_ID = 'accounting';
export const TAX_CALENDAR_APP_ID = 'tax-calendar';
export const POS_APP_ID = 'pos';
export const JOURNALS_APP_ID = 'journals';
export const REPORTS_APP_ID = 'reports';
export const API_KEYS_APP_ID = 'api-keys';
export const EVENTS_APP_ID = 'events';
export const FILE_MANAGER_APP_ID = 'file-manager';
export const BUSINESS_CALENDAR_APP_ID = 'business-calendar';
export const SHOP_APP_ID = 'shop';
export const INSTOREQR_APP_ID = 'instoreqr';
export const RESERVE_APP_ID = 'reserve';
export const HRIS_APP_ID = 'hris';

/**
 * Manage Products (`/pos/products`) is shared by POS, InStoreQR, and Shop.
 * POS is last so Apps Store deep-link prefers the POS catalog entry when none
 * of these apps are installed.
 */
export const PRODUCTS_WORKSPACE_APP_IDS = [
  INSTOREQR_APP_ID,
  SHOP_APP_ID,
  POS_APP_ID,
] as const;

/** Accrual-only standalone modules. */
export const ACCRUAL_BOOKS_APP_IDS = [BILLS_APP_ID, INVOICES_APP_ID] as const;

/** Every standalone module available through Cash or Accrual Books. */
export const SLIM_BOOKS_APP_IDS = ACCRUAL_BOOKS_BUNDLE_APP_IDS;

/** Journals hub + related books: Accounting or Journals. */
export const JOURNALS_WORKSPACE_APP_IDS = [
  ACCOUNTING_APP_ID,
  JOURNALS_APP_ID,
] as const;

/**
 * Standalone journal-side modules plus legacy full-workspace apps.
 */
export const NON_VAT_BOOKS_JOURNAL_APP_IDS = [
  ACCOUNTING_APP_ID,
  JOURNALS_APP_ID,
  CASH_RECEIPTS_JOURNAL_APP_ID,
  CASH_DISBURSEMENTS_JOURNAL_APP_ID,
  CUSTOM_ENTRIES_APP_ID,
  CUSTOMERS_APP_ID,
  SUPPLIERS_APP_ID,
  CHART_OF_ACCOUNTS_APP_ID,
  PPE_APP_ID,
  CAPITALS_APP_ID,
  BILLS_APP_ID,
  INVOICES_APP_ID,
] as const;

/** AR invoices / invoice payments: Accounting, Invoices, or accrual books. */
export const INVOICES_WORKSPACE_APP_IDS = [
  ACCOUNTING_APP_ID,
  NON_VAT_ACCRUAL_BOOKS_APP_ID,
  INVOICES_APP_ID,
] as const;

/** AP bills / bill payments: Accounting, Journals, or accrual books. */
export const BILLS_WORKSPACE_APP_IDS = [
  ACCOUNTING_APP_ID,
  JOURNALS_APP_ID,
  NON_VAT_ACCRUAL_BOOKS_APP_ID,
  BILLS_APP_ID,
] as const;

/** Reports hub: Accounting or Reports. */
export const REPORTS_WORKSPACE_APP_IDS = [
  ACCOUNTING_APP_ID,
  REPORTS_APP_ID,
] as const;

/**
 * Journals hub page: visible when any journal-side module is installed.
 */
export const JOURNALS_HUB_APP_IDS = [
  ...NON_VAT_BOOKS_JOURNAL_APP_IDS,
  NON_VAT_ACCRUAL_BOOKS_APP_ID,
  NON_VAT_BOOKS_APP_ID,
] as const;

/**
 * Reports hub page: full reports legacy apps or either standalone report app.
 */
export const REPORTS_HUB_APP_IDS = [
  ACCOUNTING_APP_ID,
  REPORTS_APP_ID,
  NON_VAT_ACCRUAL_BOOKS_APP_ID,
  NON_VAT_BOOKS_APP_ID,
  FINANCIAL_STATEMENTS_APP_ID,
  GENERAL_LEDGER_APP_ID,
  LEDGER_REPORTS_APP_ID,
] as const;

/**
 * Standalone report modules plus legacy full-report apps.
 */
export const NON_VAT_BOOKS_REPORT_APP_IDS = [
  ACCOUNTING_APP_ID,
  REPORTS_APP_ID,
  FINANCIAL_STATEMENTS_APP_ID,
  GENERAL_LEDGER_APP_ID,
  LEDGER_REPORTS_APP_ID,
] as const;

export const UPLOAD_ENTRY_WORKSPACE_APP_IDS = [
  NON_VAT_ACCRUAL_BOOKS_APP_ID,
  NON_VAT_BOOKS_APP_ID,
  UPLOAD_ENTRY_APP_ID,
] as const;

export const ACTIVITY_LOGS_WORKSPACE_APP_IDS = [
  NON_VAT_ACCRUAL_BOOKS_APP_ID,
  NON_VAT_BOOKS_APP_ID,
  ACTIVITY_LOGS_APP_ID,
] as const;

type AppScopedPathRule = {
  prefix: string;
  appIds: readonly string[];
};

const CASH_VOUCHERS_PATH_PREFIX = '/cash-vouchers';

/**
 * Longest prefix first so nested workspaces (e.g. shop manage) win over a
 * shorter sibling prefix if both are ever listed.
 */
const APP_SCOPED_PATH_RULES: AppScopedPathRule[] = [
  {
    prefix: ROUTES.SETTINGS.TRANSACTION_PERIOD_LOCKS,
    appIds: [
      ACCOUNTING_APP_ID,
      JOURNALS_APP_ID,
      NON_VAT_BOOKS_APP_ID,
      NON_VAT_ACCRUAL_BOOKS_APP_ID,
      CHART_OF_ACCOUNTS_APP_ID,
    ],
  },
  { prefix: ROUTES.SHOP.MANAGE.ROOT, appIds: [SHOP_APP_ID] },
  { prefix: '/accounts-receivable', appIds: INVOICES_WORKSPACE_APP_IDS },
  { prefix: '/accounts-payable', appIds: BILLS_WORKSPACE_APP_IDS },
  {
    prefix: ROUTES.JOURNALS.CUSTOMERS,
    appIds: [
      ACCOUNTING_APP_ID,
      JOURNALS_APP_ID,
      NON_VAT_ACCRUAL_BOOKS_APP_ID,
      NON_VAT_BOOKS_APP_ID,
      CUSTOMERS_APP_ID,
    ],
  },
  {
    prefix: ROUTES.JOURNALS.SUPPLIERS,
    appIds: [
      ACCOUNTING_APP_ID,
      JOURNALS_APP_ID,
      NON_VAT_ACCRUAL_BOOKS_APP_ID,
      NON_VAT_BOOKS_APP_ID,
      SUPPLIERS_APP_ID,
    ],
  },
  { prefix: ROUTES.JOURNALS.ROOT, appIds: JOURNALS_WORKSPACE_APP_IDS },
  {
    prefix: ROUTES.TRANSACTIONS.ROOT,
    appIds: [
      ACCOUNTING_APP_ID,
      JOURNALS_APP_ID,
      NON_VAT_ACCRUAL_BOOKS_APP_ID,
      NON_VAT_BOOKS_APP_ID,
      CUSTOM_ENTRIES_APP_ID,
    ],
  },
  {
    prefix: ROUTES.CATEGORIES.ROOT,
    appIds: [
      ACCOUNTING_APP_ID,
      JOURNALS_APP_ID,
      NON_VAT_ACCRUAL_BOOKS_APP_ID,
      NON_VAT_BOOKS_APP_ID,
      CHART_OF_ACCOUNTS_APP_ID,
    ],
  },
  {
    prefix: ROUTES.CASH_RECEIPTS_JOURNAL.ROOT,
    appIds: [
      ACCOUNTING_APP_ID,
      JOURNALS_APP_ID,
      NON_VAT_ACCRUAL_BOOKS_APP_ID,
      NON_VAT_BOOKS_APP_ID,
      CASH_RECEIPTS_JOURNAL_APP_ID,
    ],
  },
  {
    prefix: ROUTES.CASH_DISBURSEMENT_JOURNAL.ROOT,
    appIds: [
      ACCOUNTING_APP_ID,
      JOURNALS_APP_ID,
      NON_VAT_ACCRUAL_BOOKS_APP_ID,
      NON_VAT_BOOKS_APP_ID,
      CASH_DISBURSEMENTS_JOURNAL_APP_ID,
    ],
  },
  { prefix: CASH_VOUCHERS_PATH_PREFIX, appIds: JOURNALS_WORKSPACE_APP_IDS },
  {
    prefix: ROUTES.EQUIPMENT.ROOT,
    appIds: [
      ACCOUNTING_APP_ID,
      JOURNALS_APP_ID,
      NON_VAT_ACCRUAL_BOOKS_APP_ID,
      NON_VAT_BOOKS_APP_ID,
      PPE_APP_ID,
    ],
  },
  {
    prefix: ROUTES.CAPITALS.ROOT,
    appIds: [
      ACCOUNTING_APP_ID,
      JOURNALS_APP_ID,
      NON_VAT_ACCRUAL_BOOKS_APP_ID,
      NON_VAT_BOOKS_APP_ID,
      CAPITALS_APP_ID,
    ],
  },
  {
    prefix: ROUTES.REPORTS.INCOME_STATEMENT,
    appIds: [
      ACCOUNTING_APP_ID,
      REPORTS_APP_ID,
      NON_VAT_ACCRUAL_BOOKS_APP_ID,
      NON_VAT_BOOKS_APP_ID,
      FINANCIAL_STATEMENTS_APP_ID,
    ],
  },
  {
    prefix: ROUTES.REPORTS.BALANCE_SHEET,
    appIds: [
      ACCOUNTING_APP_ID,
      REPORTS_APP_ID,
      NON_VAT_ACCRUAL_BOOKS_APP_ID,
      NON_VAT_BOOKS_APP_ID,
      FINANCIAL_STATEMENTS_APP_ID,
    ],
  },
  {
    prefix: ROUTES.REPORTS.STATEMENT_OF_CHANGES_IN_EQUITY,
    appIds: [
      ACCOUNTING_APP_ID,
      REPORTS_APP_ID,
      NON_VAT_ACCRUAL_BOOKS_APP_ID,
      NON_VAT_BOOKS_APP_ID,
      FINANCIAL_STATEMENTS_APP_ID,
    ],
  },
  {
    prefix: ROUTES.REPORTS.CASH_FLOW_STATEMENT,
    appIds: [
      ACCOUNTING_APP_ID,
      REPORTS_APP_ID,
      NON_VAT_ACCRUAL_BOOKS_APP_ID,
      NON_VAT_BOOKS_APP_ID,
      FINANCIAL_STATEMENTS_APP_ID,
    ],
  },
  {
    prefix: ROUTES.REPORTS.TRIAL_BALANCE,
    appIds: [
      ACCOUNTING_APP_ID,
      REPORTS_APP_ID,
      NON_VAT_ACCRUAL_BOOKS_APP_ID,
      NON_VAT_BOOKS_APP_ID,
      FINANCIAL_STATEMENTS_APP_ID,
    ],
  },
  {
    prefix: ROUTES.REPORTS.GENERAL_LEDGER,
    appIds: [
      ACCOUNTING_APP_ID,
      REPORTS_APP_ID,
      NON_VAT_ACCRUAL_BOOKS_APP_ID,
      NON_VAT_BOOKS_APP_ID,
      LEDGER_REPORTS_APP_ID,
      GENERAL_LEDGER_APP_ID,
    ],
  },
  {
    prefix: ROUTES.REPORTS.GENERAL_JOURNAL,
    appIds: [
      ACCOUNTING_APP_ID,
      REPORTS_APP_ID,
      NON_VAT_ACCRUAL_BOOKS_APP_ID,
      NON_VAT_BOOKS_APP_ID,
      LEDGER_REPORTS_APP_ID,
    ],
  },
  {
    prefix: ROUTES.REPORTS.CASH_RECEIPTS_JOURNAL,
    appIds: [
      ACCOUNTING_APP_ID,
      REPORTS_APP_ID,
      NON_VAT_ACCRUAL_BOOKS_APP_ID,
      NON_VAT_BOOKS_APP_ID,
      LEDGER_REPORTS_APP_ID,
    ],
  },
  {
    prefix: ROUTES.REPORTS.CASH_DISBURSEMENT_JOURNAL,
    appIds: [
      ACCOUNTING_APP_ID,
      REPORTS_APP_ID,
      NON_VAT_ACCRUAL_BOOKS_APP_ID,
      NON_VAT_BOOKS_APP_ID,
      LEDGER_REPORTS_APP_ID,
    ],
  },
  { prefix: ROUTES.REPORTS.ROOT, appIds: REPORTS_WORKSPACE_APP_IDS },
  { prefix: ROUTES.TAX_COMPLIANCE, appIds: [TAX_CALENDAR_APP_ID] },
  { prefix: ROUTES.BUSINESS_CALENDAR, appIds: [BUSINESS_CALENDAR_APP_ID] },
  { prefix: ROUTES.EVENTS.ROOT, appIds: [EVENTS_APP_ID] },
  { prefix: ROUTES.FILE_MANAGER, appIds: [FILE_MANAGER_APP_ID] },
  { prefix: ROUTES.INSTOREQR.ROOT, appIds: [INSTOREQR_APP_ID] },
  { prefix: ROUTES.RESERVE.ROOT, appIds: [RESERVE_APP_ID] },
  // Longer than `/pos` so products stays available without installing POS.
  { prefix: ROUTES.POS.PRODUCTS, appIds: PRODUCTS_WORKSPACE_APP_IDS },
  { prefix: ROUTES.POS.ROOT, appIds: [POS_APP_ID] },
  { prefix: ROUTES.PORTAL.ROOT, appIds: [HRIS_APP_ID] },
  { prefix: ROUTES.HRIS.ROOT, appIds: [HRIS_APP_ID] },
  { prefix: ROUTES.UPLOAD_ENTRY, appIds: UPLOAD_ENTRY_WORKSPACE_APP_IDS },
  { prefix: ROUTES.ACTIVITY_LOGS, appIds: ACTIVITY_LOGS_WORKSPACE_APP_IDS },
  { prefix: ROUTES.API_KEYS, appIds: [API_KEYS_APP_ID] },
].sort((a, b) => b.prefix.length - a.prefix.length);

function normalizePathname(path: string): string {
  const withoutQuery = path.split('?')[0] ?? path;
  if (withoutQuery.length > 1 && withoutQuery.endsWith('/')) {
    return withoutQuery.slice(0, -1);
  }
  return withoutQuery;
}

function pathnameMatchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/** App ids that must be installed (OR) for this path, or null if not app-scoped. */
export function getRequiredAppIdsForPath(pathname: string): string[] | null {
  const normalized = normalizePathname(pathname);
  // Exact hub pages — slim books packs unlock these without the full workspace.
  if (normalized === ROUTES.JOURNALS.ROOT) {
    return [...JOURNALS_HUB_APP_IDS];
  }
  if (normalized === ROUTES.REPORTS.ROOT) {
    return [...REPORTS_HUB_APP_IDS];
  }
  const match = APP_SCOPED_PATH_RULES.find((rule) =>
    pathnameMatchesPrefix(normalized, rule.prefix),
  );
  return match ? [...match.appIds] : null;
}

export function isPathAllowedForInstalledApps(
  pathname: string,
  isAppInstalled: (appId: string) => boolean,
): boolean {
  const requiredAppIds = getRequiredAppIdsForPath(pathname);
  if (!requiredAppIds) {
    return true;
  }
  return requiredAppIds.some((appId) => isAppInstalled(appId));
}

/**
 * Catalog app to open in Apps Store when this path is not installed.
 * Workspace paths (accounting OR journals/invoices/reports) use the dedicated
 * module id, not the Accounting bundle.
 */
export function getAppsStoreAppIdForPath(pathname: string): string | null {
  const requiredAppIds = getRequiredAppIdsForPath(pathname);
  if (!requiredAppIds || requiredAppIds.length === 0) {
    return null;
  }
  return requiredAppIds[requiredAppIds.length - 1] ?? null;
}

export function isAnySlimBooksAppInstalled(
  isAppInstalled: (appId: string) => boolean,
): boolean {
  return (
    isAppInstalled(NON_VAT_BOOKS_APP_ID) ||
    isAppInstalled(NON_VAT_ACCRUAL_BOOKS_APP_ID) ||
    SLIM_BOOKS_APP_IDS.some((appId) => isAppInstalled(appId))
  );
}

export function isAnyAccrualBooksAppInstalled(
  isAppInstalled: (appId: string) => boolean,
): boolean {
  return (
    isAppInstalled(NON_VAT_ACCRUAL_BOOKS_APP_ID) ||
    ACCRUAL_BOOKS_APP_IDS.some((appId) => isAppInstalled(appId))
  );
}
